import { Component, OnInit, OnDestroy, Input, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { StrategyService } from '../../services/strategy.service';
import { Subscription } from 'rxjs';
import { Pillar } from '../../interfaces/models/strategy.model';
import { Value } from '../../interfaces/models/value.model';
import { trigger, style, transition, animate, state } from '@angular/animations';

@Component({
  selector: 'app-main',
  standalone: true,
  imports: [CommonModule, RouterModule],
  templateUrl: './main.component.html',
  styleUrls: ['./main.component.scss'],
  animations: [
    // Modal fade in/out animation
    trigger('fadeInOut', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate('300ms ease-out', style({ opacity: 1 }))
      ]),
      transition(':leave', [
        animate('200ms ease-in', style({ opacity: 0 }))
      ])
    ]),
    // Modal slide in animation
    trigger('slideIn', [
      transition(':enter', [
        style({ transform: 'translateY(-50px)', opacity: 0 }),
        animate('400ms cubic-bezier(0.4, 0.0, 0.2, 1)', style({ transform: 'translateY(0)', opacity: 1 }))
      ]),
      transition(':leave', [
        animate('300ms ease-in', style({ transform: 'translateY(-30px)', opacity: 0 }))
      ])
    ]),
    // Fade in animation
    trigger('fadeIn', [
      transition(':enter', [
        style({ opacity: 0, transform: 'scale(0.95)' }),
        animate('250ms ease-out', style({ opacity: 1, transform: 'scale(1)' }))
      ])
    ]),
    // Fade in up animation for pillar sections
    trigger('fadeInUp', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(20px)' }),
        animate('400ms ease-out', style({ opacity: 1, transform: 'translateY(0)' }))
      ])
    ])
  ]
})
export class MainComponent implements OnInit, OnDestroy {
  @Input() vision: string = '';
  @Input() mission: string = '';

  // Values data
  @Input() values: Array<Value> = [];

  // Pillars data
  @Input() pillars: Array<Pillar & { pillar_name?: string }> = [];

  // Values circle positions with descriptions
  @Input() valuesCircle: Array<{
    name: string;
    icon: string;
    description?: string;
  }> = [];

  // Modal state
  isPillarsModalOpen: boolean = false;
  isValueModalOpen: boolean = false;

  // Selected value index for tooltip
  selectedValueIndex: number | null = null;
  selectedValue: { name: string; icon: string; description?: string } | null = null;

  private subscriptions = new Subscription();

  constructor(
    private strategyService: StrategyService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    const sub = this.strategyService.defaultStrategy$.subscribe((strategy: any) => {
      if (!strategy) return;

      // Set vision and mission
      this.vision = strategy.vision || this.vision;
      this.mission = strategy.mission || this.mission;

      // Set values
      this.values = (strategy.values || []) as Value[];

      // Set pillars
      this.pillars = ((strategy.pillars || []) as Pillar[]).map((p) => ({
        ...p,
        pillar_name: (p as any).name ?? (p as any).pillar_name
      }));

      // Build values circle if not provided
      if (!this.valuesCircle?.length) {
        this.valuesCircle = this.buildValuesCircle(this.values);
      }

      this.cdr.markForCheck();
    });

    this.subscriptions.add(sub);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  /**
   * Open pillars modal
   */
  openPillarsModal(): void {
    this.isPillarsModalOpen = true;
    // Prevent body scroll when modal is open
    document.body.style.overflow = 'hidden';
    this.cdr.markForCheck();
  }

  /**
   * Close pillars modal
   */
  closePillarsModal(): void {
    this.isPillarsModalOpen = false;
    // Restore body scroll
    document.body.style.overflow = '';
    this.cdr.markForCheck();
  }

  /**
   * Toggle value detail tooltip
   */
  toggleValueDetail(index: number): void {
    if (this.selectedValueIndex === index) {
      this.selectedValueIndex = null;
    } else {
      this.selectedValueIndex = index;
    }
    this.cdr.markForCheck();
  }

  /**
   * Open value detail modal
   */
  openValueModal(value: { name: string; icon: string; description?: string }): void {
    this.selectedValue = value;
    this.isValueModalOpen = true;
    // Prevent body scroll when modal is open
    document.body.style.overflow = 'hidden';
    this.cdr.markForCheck();
  }

  /**
   * Close value detail modal
   */
  closeValueModal(): void {
    this.isValueModalOpen = false;
    this.selectedValue = null;
    // Restore body scroll
    document.body.style.overflow = '';
    this.cdr.markForCheck();
  }

  /**
   * Build values circle with default descriptions
   */
  private buildValuesCircle(values: Array<Value>): Array<{
    name: string;
    icon: string;
    description?: string;
  }> {
    if (!values || values.length === 0) {
      // Default values if none provided - matching the image layout
      // Position 1 (top): التمكين
      // Position 2 (left): الإبتكار
      // Position 3 (right): الحوكمة
      // Position 4 (bottom-left): الريادة
      // Position 5 (bottom-right): الاستدامة
      return [
        { name: 'التمكين', icon: 'fa-solid fa-handshake', description: 'تعزيز مشاركة فرق العمل في مطار الشارقة' },
        { name: 'الإبتكار', icon: 'fa-solid fa-lightbulb', description: 'تقديم حلول تشغيلية ذكية' },
        { name: 'الحوكمة', icon: 'fa-solid fa-scale-balanced', description: 'الشفافية والمساءلة في العمل' },
        { name: 'الريادة', icon: 'fa-solid fa-rocket', description: 'التميز في خدمات مطار الشارقة' },
        { name: 'الاستدامة', icon: 'fa-solid fa-seedling', description: 'ضمان استدامة العمليات والخدمات' }
      ];
    }

    return values.map(v => ({
      name: v.name,
      icon: (v as any).icon || 'pi pi-circle-fill',
      description: (v as any).description || v.name
    }));
  }

  /**
   * TrackBy functions for performance optimization
   */
  trackByPillar = (_: number, p: any) => p?.id ?? p?.pillar_name ?? p?.name;
  trackByGoal = (_: number, g: any) => g?.id ?? g?.name;
  trackByValuePoint = (_: number, v: any) => v?.name;
}
